import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Leaf, 
  Calculator, 
  BookOpen, 
  Calendar, 
  TrendingUp, 
  Users, 
  Globe, 
  Award,
  ArrowRight,
  CheckCircle,
  Shield,
  Target,
  Zap,
  BarChart3
} from 'lucide-react';
import FadeIn from '../components/animations/FadeIn';
import StaggerWrapper from '../components/animations/StaggerWrapper';
import Slider from "react-slick";
import 'animate.css';


const Home = () => {
  // ---- Counter-up animation (easeOut) for stats ----
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const useInViewOnce = (options = { threshold: 0.35, rootMargin: '0px' }) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
      const el = ref.current;
      if (!el || inView) return;

      const observer = new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        },
        options
      );

      observer.observe(el);
      return () => observer.disconnect();
    }, [inView, options]);

    return [ref, inView];
  };

  const AnimatedNumber = ({
    value,
    duration = 3000,
    ease = easeOutCubic,
    start = 0,
    format,
    inView
  }) => {
    const [display, setDisplay] = useState(start);

    useEffect(() => {
      if (!inView) return;

      let rafId;
      const from = Number.isFinite(start) ? start : 0;
      const to = Number.isFinite(value) ? value : 0;
      const startTime = performance.now();

      const tick = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        const eased = ease(t);
        const current = from + (to - from) * eased;

        // whole-number feel like arcade counters
        setDisplay(Math.round(current));

        if (t < 1) {
          rafId = requestAnimationFrame(tick);
        }
      };

      // reset on trigger so the animation always starts at 0
      setDisplay(from);
      rafId = requestAnimationFrame(tick);

      return () => {
        if (rafId) cancelAnimationFrame(rafId);
      };
    }, [inView, value, duration, ease, start]);

    return <>{format ? format(display) : display}</>;
  };
  const features = [
    {
      icon: Calculator,
      title: 'Carbon Footprint Calculator',
      description: 'Calculate your environmental impact with specialized trackers for NSFs and Singaporean youth (school & university). Track emissions from daily activities, transport, and lifestyle choices.',
      href: '/carbon-tracker'
    },
    {
      icon: BookOpen,
      title: 'Education Hub',
      description: 'Learn about Singapore’s climate goals and how Singaporean youth can contribute. Explore policies, initiatives, and practical guides for greener daily choices.',
      href: '/education'
    },
    {
      icon: Calendar,
      title: 'Eco-Events Calendar',
      description: 'Join green initiatives across camps, schools, and communities. From cleanups to eco-volunteering, turn everyday life into environmental action.',
      href: '/events'
    },
    {
      icon: Users,
      title: 'Profile',
      description: 'Track progress, earn eco-badges, and compare sustainability goals with friends, schoolmates, and NSF units through challenges and leaderboards.',
      href: '/profile'
    }
  ];

  // Roulette features array (only 3)
  const rouletteFeatures = [
    {
      icon: Calculator,
      title: 'Carbon Footprint Calculator',
      description: 'Calculate your environmental impact with specialized trackers for NSFs and Singaporean youth (school & university). Track emissions from daily activities, transport, and lifestyle choices.',
      href: '/carbon-tracker'
    },
    {
      icon: Calendar,
      title: 'Eco-Events Calendar',
      description: 'Join green initiatives across camps, schools, and communities. From cleanups to eco-volunteering, turn everyday life into environmental action.',
      href: '/events'
    },
    {
      icon: BookOpen,
      title: 'Education Hub',
      description: 'Learn about Singapore’s climate goals and how Singaporean youth can contribute. Explore policies, initiatives, and practical guides for greener daily choices.',
      href: '/education'
    }
  ];
  // RouletteCarousel component
  const RouletteCarousel = () => {
    const sliderRef = React.useRef(null);
    const [isMobile, setIsMobile] = React.useState(() => {
      if (typeof window === 'undefined') return false;
      return window.matchMedia('(max-width: 768px)').matches;
    });

    React.useEffect(() => {
      if (typeof window === 'undefined') return;
      const mq = window.matchMedia('(max-width: 768px)');
      const handler = (e) => setIsMobile(e.matches);
      // modern + fallback
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else mq.addListener(handler);
      setIsMobile(mq.matches);
      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', handler);
        else mq.removeListener(handler);
      };
    }, []);

    const settings = {
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: isMobile ? 1 : 3,
      slidesToScroll: 1,
      centerMode: !isMobile,
      centerPadding: '0px',
      arrows: false,
      initialSlide: isMobile ? 0 : 1,
      focusOnSelect: false,
      accessibility: false,
      pauseOnFocus: false,
    };
    return (
      <Slider key={isMobile ? 'mobile' : 'desktop'} ref={sliderRef} {...settings} className="roulette-carousel">
        {rouletteFeatures.map((feature, idx) => {
          const Icon = feature.icon;
          return (
            <div
              className="roulette-slide"
              key={idx}
              onClick={() => sliderRef.current?.slickGoTo(idx)}
            >
              <div className="roulette-card bg-white border border-gray-200 rounded-2xl p-4 sm:p-7 min-h-[200px] sm:min-h-[220px] flex flex-col min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-50 flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0">
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-green-600" />
                </div>
                <h3 className="roulette-title text-base sm:text-xl font-semibold text-gray-900 mb-2 break-words">
                  {feature.title}
                </h3>
                <p className="roulette-desc text-gray-600 text-sm sm:text-base leading-relaxed mb-4 flex-grow break-words min-w-0">
                  {feature.description}
                </p>
                <Link
                  to={feature.href}
                  className="flex items-center text-green-700 text-sm font-medium mt-auto hover:text-green-800"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span>Explore</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
          );
        })}
      </Slider>
    );
  };

  const stats = [
    { label: 'Carbon Footprints Tracked', value: 140, suffix: '+', icon: Calculator },
    { label: 'Eco-Events Hosted', value: 3, suffix: '', icon: Calendar },
    { label: 'Active Users', value: 50, suffix: '', icon: Users },
    { label: 'CO₂ Saved (tonnes)', value: 200, suffix: '+', icon: Leaf }
  ];

  // Trigger stats counter animation once when the stats section enters view
  const [statsRef, statsInView] = useInViewOnce({ threshold: 0.35 });

  const benefits = [
    'Track your environmental impact',
    'Learn about local sustainability initiatives',
    'Join local environmental initiatives',
    "Contribute to Singapore's green goals"
  ];

  const partners = [
    {
      name: 'SG Eco Fund',
      logo: '/images/sg-eco-fund-logo.png',
      alt: 'SG Eco Fund Logo'
    },
    {
      name: 'WWF Singapore',
      logo: '/images/wwf-logo.png', 
      alt: 'WWF Singapore Logo'
    },
    {
      name: 'Ministry of Sustainability and the Environment',
      logo: '/images/MSE-logo.png',
      alt: 'Ministry of Sustainability and the Environment Logo'
    },
    {
      name: 'WWF WeGotThis',
      logo: '/images/wegotthis.gif',
      alt: 'Wegotthis Logo'
    },
  ];

  // Array of images to show in the hero carousel
  const heroImages = [
    "/images/gardens-by-the-bay.jpg",
    "/images/jewel-changi.jpg",
    "/images/marsiling-park.jpg"
  ];


// Hero carousel component
  const HeroCarousel = () => {
    const settings = {
      dots: true,             // small dots at bottom
      infinite: true,         // loops forever
      autoplay: true,         // slides change automatically
      autoplaySpeed: 5000,    // every 5s
      speed: 800,             // transition speed
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,          // no arrows, just dots (Tesla style)
      fade: true,             // smooth fade animation
      pauseOnHover: false,
    };

    return (
      <Slider {...settings} className="relative custom-carousel hero-carousel">
        {heroImages.map((src, index) => (
          <div key={index} className="relative hero-carousel-slide">
            <img
              src={src}
              alt={`Slide ${index + 1}`}
              className="w-full h-full object-cover hero-carousel-img"
            />
            {/* Dark overlay so text is readable */}
            <div className="absolute inset-0 bg-black bg-opacity-70 hero-carousel-overlay"></div>
          </div>
        ))}
      </Slider>
    );
  };

  return (
    <>
      <style>{`
         /* HERO carousel: viewport minus navbar */
         :root {
           --navbar-height: 72px;
         }

         .hero-carousel,
         .hero-carousel .slick-list,
         .hero-carousel .slick-track {
           height: calc(100vh - var(--navbar-height));
         }

         .hero-carousel-slide {
           height: calc(100vh - var(--navbar-height));
         }

         .hero-carousel-img {
           height: 100%;
         }

         .hero-overlay-container {
           height: calc(100vh - var(--navbar-height));
         }

         /* Mobile hero: shorter, tighter, and visually balanced */
         @media (max-width: 640px) {
           :root {
             --navbar-height: 64px;
           }
           .hero-carousel,
           .hero-carousel .slick-list,
           .hero-carousel .slick-track,
           .hero-carousel-slide,
           .hero-overlay-container {
             height: min(72svh, 640px);
           }

           .hero-overlay-container {
             justify-content: flex-start;
             padding-top: calc(var(--navbar-height) + 2rem);
             padding-bottom: 4.5rem;
           }

           .hero-carousel-img {
             object-position: center;
           }
         }

         /* Hero carousel dots: larger and visible on mobile */
         .custom-carousel .slick-dots li button:before {
           font-size: 14px !important;
           opacity: 0.7;
           color: white !important;
         }
         .custom-carousel .slick-dots li.slick-active button:before {
           opacity: 1;
           color: white !important;
         }
         @media (min-width: 640px) {
           .custom-carousel .slick-dots li button:before {
             font-size: 12px !important;
           }
         }
         /* Roulette carousel */
         .roulette-carousel .slick-dots {
           position: relative;
           bottom: 0;
           margin-top: 16px;
         }
         .roulette-carousel .slick-dots li button:before {
           font-size: 12px;
           opacity: 0.35;
           color: #16a34a; /* green-600 */
         }
         @media (min-width: 640px) {
           .roulette-carousel .slick-dots li button:before {
             font-size: 10px;
           }
         }
         .roulette-carousel .slick-dots li.slick-active button:before {
           opacity: 1;
           color: #16a34a;
         }
         .roulette-slide {
           padding: 10px;
         }

         /* Mobile: make the centered card much wider */
         @media (max-width: 768px) {
           .roulette-slide {
             padding: 0;
           }
           .roulette-card {
             width: 100%;
           }
         }
         .roulette-card {
           opacity: 1;
           transform: scale(1);
           background: #ffffff;
           overflow: hidden;
           transition: opacity 250ms ease, transform 250ms ease, box-shadow 250ms ease, background-color 250ms ease;
         }

         /* Keep full text visible on mobile */
         .roulette-title,
         .roulette-desc {
           overflow-wrap: anywhere;
           word-break: normal;
         }

         /* Hover preview: make hovered card fully visible + pop + light green */
         .roulette-slide:hover .roulette-card {
           opacity: 1;
           transform: scale(1);
           background-color:rgb(255, 255, 255); 
           box-shadow: 0 20px 50px rgba(0,0,0,0.15);
         }

         /* Desktop: non-centered cards are dimmed; center card pops */
         @media (min-width: 769px) {
           .roulette-carousel .slick-slide .roulette-card {
             opacity: 0.45;
             transform: scale(0.9);
             box-shadow: none;
           }
           .roulette-carousel .slick-center .roulette-card {
             opacity: 1;
             transform: scale(1);
             box-shadow: 0 20px 50px rgba(0,0,0,0.15);
             background: #ffffff;
           }
         }

         /* If the centered slide is hovered, apply the same hover styles */
         .roulette-carousel .slick-center:hover .roulette-card {
           opacity: 1;
           transform: scale(1);
           background-color:rgb(255, 255, 255); /* tailwind green-200 */
           box-shadow: 0 20px 50px rgba(0,0,0,0.15);
         }
         .custom-carousel .slick-dots {
           position: absolute;
           bottom: 16px;
           left: 0;
           right: 0;
           margin: 0 auto;
           z-index: 5;
         }
         .custom-carousel .slick-dots li {
           margin: 0 6px;
         }
         @media (max-width: 639px) {
           .custom-carousel .slick-dots {
             bottom: 18px;
           }
         }
         @media (min-width: 640px) {
           .custom-carousel .slick-dots {
             bottom: 70px;
           }
         }
         @media (min-width: 1024px) {
           .custom-carousel .slick-dots {
             bottom: 30px;
           }
         }
         @media (min-width: 1280px) {
           .custom-carousel .slick-dots {
             bottom: 40px;
           }
         }
      `}</style>
      <div className="overflow-x-hidden w-full max-w-[100vw]">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <FadeIn>
            <HeroCarousel />
            <div className="absolute inset-0 hero-overlay-container flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 overflow-hidden">
              <FadeIn delay={0.6}>
                <div className="flex justify-center mb-1 sm:mb-6">
                  <div className="w-10 h-10 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center relative overflow-hidden bg-transparent flex-shrink-0">
                    <img
                      src="/images/apple-touch-icon.png"
                      alt="ClimateHub leaf logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </FadeIn>
              
                <h1 className="text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-1 sm:mb-6 leading-tight animate__animated animate__fadeInDown break-words max-w-full">
                  ClimateHub
                </h1>
              
              <FadeIn delay={1.0}>
                <h2 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-semibold text-green-200 mb-2 sm:mb-4 px-2 leading-snug break-words max-w-[19rem] sm:max-w-full">
                  Environmental Action, Made Easy
                </h2>
              </FadeIn>
              <FadeIn delay={1.2}>
                <p className="text-sm sm:text-lg md:text-xl text-gray-100 mb-4 sm:mb-8 max-w-[17rem] sm:max-w-2xl lg:max-w-3xl mx-auto leading-relaxed px-2 break-words">
                Singapore's one-stop platform for environmental action
                </p>
              </FadeIn>
              <FadeIn delay={1.4}>
                <div className="flex justify-center w-full px-4 mb-4 sm:mb-8">
                  <Link
                    to="/carbon-tracker"
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold 
                      py-3 sm:py-4 px-5 sm:px-8 
                      rounded-xl text-base sm:text-lg 
                      transition-colors duration-200 
                      inline-flex items-center justify-center gap-2 
                      w-full max-w-[260px] sm:max-w-sm md:w-auto min-h-[48px] shadow-lg"
                  >
                    <span className="break-words">Calculate your footprint</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  </Link>
                </div>
              </FadeIn>
            </div>
          </FadeIn>
        </section>

        {/* Roulette Feature Section — on mobile: one card, full width; no cramped columns */}
        <section className="py-10 sm:py-16 bg-gradient-to-b from-green-200 via-emerald-50 to-emerald-200 border-t border-emerald-100 overflow-hidden">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 w-full box-border">
            <div className="text-center mb-6 sm:mb-10">
              <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 break-words px-2 max-w-full">
                Everything you need to start your own action journey
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto break-words px-2">
                Built for Singaporean youth — NSFs, school and university students — to track impact, learn fast, and take action.
              </p>
            </div>

            <div className="roulette-carousel w-full max-w-full">
              <RouletteCarousel />
            </div>
          </div>
        </section>


        {/* Partners & Stats Combined Section */}
        <section className="py-16 bg-gradient-to-b from-emerald-200 via-green-100 to-green-100 border-t border-emerald-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* LEFT — PARTNERS WITH TITLE */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Recognised & Supported By
              </h2>
              <p className="text-gray-600 text-sm sm:text-base mb-6">
                ClimateHub is a proud recipient of the SG Eco Fund and collaborates with leading national and international sustainability organisations.
              </p>

              <div className="flex flex-wrap items-center gap-6 sm:gap-8">
                {partners.map((partner, index) => (
                  <div key={index} className="flex items-center justify-center">
                    <img
                      src={partner.logo}
                      alt={partner.alt}
                      className="h-10 sm:h-12 md:h-14 object-contain transition-all duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — STATISTICS GRID */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                Making a Difference Together
              </h2>

              <div ref={statsRef} className="grid grid-cols-2 gap-6">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="p-4 border rounded-xl bg-white shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-center mb-3">
                      <stat.icon className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      <AnimatedNumber
                        value={stat.value}
                        duration={3000}
                        inView={statsInView}
                        format={(n) => `${n}${stat.suffix || ''}`}
                      />
                    </div>
                    <div className="text-gray-600 text-xs sm:text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>

              
            </div>

          </div>
        </section>

        {/* Features Section */}

        {/* Benefits Section */}
        <section className="py-12 sm:py-14 bg-gradient-to-b from-green-100 via-cyan-50 to-cyan-100 border-t border-green-100">
          <FadeIn delay={0.4}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Centered title like the roulette section */}
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  Why Choose ClimateHub?
                </h2>
                <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                  Whether you're serving National Service or navigating student life, you can make a difference.
                  ClimateHub helps you understand and reduce your environmental impact in meaningful ways.
                </p>
              </div>

              {/* Two-column: bullets left, pangolin animation right */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-5xl mx-auto">
                {/* LEFT — POINTERS */}
                <div className="lg:pl-6">
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start space-x-3 py-1">
                        <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm sm:text-base leading-relaxed">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RIGHT — PANGOLIN IMAGE */}
                <div className="w-full flex items-center justify-center lg:pr-6">
                  <img
                    src="/images/Climate_Hub_3.png"
                    alt="Pangolin using ClimateHub to track carbon footprint"
                    className="max-w-full h-auto max-h-[260px] sm:max-h-[300px] object-contain"
                  />
                </div>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* CTA Section */}
        <section className="py-8 sm:py-16 lg:py-20 bg-cyan-100 via-cyan-200 to-cyan-300 border-t border-cyan-100">
          <FadeIn delay={0.5}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl shadow-2xl px-5 py-6 sm:p-12 text-center">
                <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-6 leading-tight max-w-[16rem] sm:max-w-none mx-auto">
                  Ready to Build Singapore's Green Future?
                </h2>
                <p className="text-sm sm:text-lg lg:text-xl text-green-100 mb-5 sm:mb-8 max-w-[16.5rem] sm:max-w-2xl mx-auto px-1 sm:px-4 leading-relaxed">
                  Join fellow Singaporeans in making every day count for the environment. 
                  Start your eco-mission today and be part of the movement towards sustainability.
                </p>
                <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-4 justify-center max-w-[15rem] sm:max-w-none mx-auto">
                  <Link
                    to="/carbon-tracker"
                    className="bg-white text-emerald-600 hover:bg-gray-100 font-semibold py-2.5 sm:py-4 px-5 sm:px-8 rounded-lg text-sm sm:text-lg transition-colors duration-200 inline-flex items-center justify-center space-x-2 w-full sm:w-auto min-h-[44px]"
                  >
                    <span>Calculate Your Footprint</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Link>
                  <Link
                    to="/events"
                    className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 font-semibold py-2.5 sm:py-4 px-5 sm:px-8 rounded-lg text-sm sm:text-lg transition-colors duration-200 inline-flex items-center justify-center space-x-2 w-full sm:w-auto min-h-[44px]"
                  >
                    <span>Explore Events</span>
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Link>
                </div>
              </div>
            </div>
          </FadeIn>
        </section>
      </div>
    </>
  );
};

export default Home;